import type {
  AgentTestChangeRowIdentifiers,
  AgentTestConfigPlan,
  AgentTestOperation,
} from '../../../../types';
import { getAgentTestChangeRowId } from '../../../../types';
import type { ToolRendererArgs } from '../../types';

// Where the plan approval renderers navigate back to; the per-type preview
// pages live beneath it.
export const TEST_MANAGEMENT_PATH = '/test-management';

export const isAgentTestOperation = (
  value: unknown,
): value is AgentTestOperation =>
  value === 'update' || value === 'create' || value === 'delete';

// Models sometimes stringify nested tool-arg arrays (especially large
// `changes` payloads). Treat a JSON array string the same as a real array so
// the plan card / preview don't render as empty.
const coerceUnknownArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export function getChangesFromToolArgs<TChange>(
  toolArgs: ToolRendererArgs,
  normalizeChanges?: (changes: TChange[]) => TChange[],
): TChange[] {
  const changes = coerceUnknownArray(toolArgs?.changes) as TChange[];
  return normalizeChanges ? normalizeChanges(changes) : changes;
}

const SKIP_REASON_LABELS: Record<string, string> = {
  already_covered: 'already covered',
  not_supported: 'not supported',
};

export const formatSkippedPlanSummary = (
  skipped: unknown,
): string | undefined => {
  const items = coerceUnknownArray(skipped).filter(
    (item): item is Record<string, unknown> =>
      !!item && typeof item === 'object' && !Array.isArray(item),
  );
  if (items.length === 0) return undefined;

  const covered = items.filter(
    (item) => item.reason === 'already_covered',
  ).length;
  const unsupported = items.filter(
    (item) => item.reason === 'not_supported',
  ).length;
  const other = items.length - covered - unsupported;
  const parts: string[] = [];
  if (covered) {
    parts.push(`${covered} ${SKIP_REASON_LABELS.already_covered}`);
  }
  if (unsupported) {
    parts.push(`${unsupported} ${SKIP_REASON_LABELS.not_supported}`);
  }
  if (other) {
    parts.push(`${other} ineligible`);
  }
  return `${items.length} skipped: ${parts.join(', ')}`;
};

export const getEnvIdFromToolArgs = (
  toolArgs: ToolRendererArgs,
): string | undefined =>
  typeof toolArgs?.env_id === 'string' ? toolArgs.env_id : undefined;

export function planFromToolArgs<TChange>(
  toolArgs: ToolRendererArgs,
  toolCallId: string,
  normalizeChanges?: (changes: TChange[]) => TChange[],
): AgentTestConfigPlan<TChange> {
  const rawOperation = toolArgs?.operation;
  return {
    toolCallId,
    envId: getEnvIdFromToolArgs(toolArgs),
    operation: isAgentTestOperation(rawOperation) ? rawOperation : 'update',
    changes: getChangesFromToolArgs(toolArgs, normalizeChanges),
    skipSummary: formatSkippedPlanSummary(toolArgs?.skipped),
    isSelectAll: true,
    excludedItems: [],
    selectedItems: [],
  };
}

export const getApprovedChanges = <
  TChange extends AgentTestChangeRowIdentifiers,
>(
  plan: AgentTestConfigPlan<TChange>,
): TChange[] =>
  plan.isSelectAll
    ? plan.changes.filter(
        (change) =>
          !plan.excludedItems.includes(getAgentTestChangeRowId(change)),
      )
    : plan.changes.filter((change) =>
        plan.selectedItems.includes(getAgentTestChangeRowId(change)),
      );

export function buildNewArgsFromPlan<
  TChange extends AgentTestChangeRowIdentifiers,
>(
  plan: AgentTestConfigPlan<TChange>,
  toolArgs: ToolRendererArgs,
): Record<string, unknown> {
  return {
    goal: String(toolArgs?.goal ?? ''),
    env_id: String(toolArgs?.env_id ?? ''),
    operation: plan.operation,
    changes: getApprovedChanges(plan),
  };
}
